import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LanguageToggle } from '@/components/LanguageToggle';

export function ParentLogin() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();

  useEffect(() => {
    // Always clear all relevant storage/cookies on mount for a fresh login state
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(';').forEach(function(c) {
      document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
    });
  }, []);

  useEffect(() => {
    // If authenticated, check if we have a valid userType_parent
    // If not, this is a cross-app session or rejected login - force logout
    if (isAuthenticated && !isLoading) {
      const userTypeParent = localStorage.getItem('userType_parent');
      if (!userTypeParent) {
        // No valid parent session - force federated logout
        logout({ logoutParams: { returnTo: globalThis.location.origin + '/parent/login', federated: true } });
      }
    }
  }, [isAuthenticated, isLoading, logout]);

  const handleParentLogin = () => {
    // Store userType BEFORE redirecting to Auth0
    localStorage.setItem('userType_parent', 'parent');
    loginWithRedirect({
      authorizationParams: {
        // Do not force a specific connection to allow Google/passwordless chooser
        prompt: 'login',
        max_age: 0,
        scope: 'openid profile email'
      }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <img src="https://pub-327c49603f8c4373ac8b69237691fafc.r2.dev/rds-logo.png" alt="Logo" className="h-28 w-28" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {t('app.title')}
          </CardTitle>
          <CardDescription>
            {t('auth.parentPortalDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={handleParentLogin} className="w-full" size="lg">
            {t('auth.loginAsParent')}
          </Button>

          <div className="text-center">
            <button
              onClick={() => {
                // Clear any stale userType before switching
                localStorage.removeItem('userType_parent');
                localStorage.removeItem('userType_doctor');
                localStorage.removeItem('auth_token');
                sessionStorage.clear();
                // Clear all cookies (including Auth0)
                document.cookie.split(';').forEach(function(c) {
                  document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
                });
                // Use full page reload to reinitialize Auth0 provider
                globalThis.location.href = '/doctor/login';
              }}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {t('auth.switchToDoctorPortal')}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

